import { HttpClient } from '@angular/common/http';
import { ApplicationRef, Inject, Injectable, OnDestroy } from '@angular/core';
import { HttpStatus } from '@cna/shared/generic-types';
import { concat, EMPTY, from, merge, Subject, timer } from 'rxjs';
import {
    catchError,
    exhaustMap,
    filter,
    take,
    takeUntil,
} from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import {
    NetworkStatus,
    NetworkStatusService,
} from '../../../network-status/src/lib/network-status.service';
import {
    TIME_TRACKING_CONFIG,
    TimeTrackingConfig,
} from './time-tracking.config';
import {
    QueuedTimeExpenditureData,
    TimeExpenditureData,
} from './time-expenditure-data';

@Injectable()
export class TimeTrackingClientService implements OnDestroy {
    private destroy$ = new Subject<void>();
    private readonly STORAGE_KEY = 'open_time_expenditure';
    private submissionQueue: Promise<void> = Promise.resolve();

    constructor(
        private networkStatusService: NetworkStatusService,
        private http: HttpClient,
        @Inject(TIME_TRACKING_CONFIG)
        private timeTrackingConfig: TimeTrackingConfig,
        applicationRef: ApplicationRef
    ) {
        concat(
            applicationRef.isStable.pipe(
                filter(isStable => isStable),
                take(1)
            ),
            merge(
                this.networkStatusService.status$.pipe(
                    filter(status => status === NetworkStatus.ONLINE)
                ),
                timer(0, 60_000) // once per minute
            )
        )
            .pipe(
                filter(() => this.hasOpenTimeTrackings()),
                takeUntil(this.destroy$),
                exhaustMap(() =>
                    from(this.submitOpenTimeTrackingsFromStorage()).pipe(
                        catchError(() => EMPTY)
                    )
                )
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    async submit(data: TimeExpenditureData): Promise<void> {
        this.persistForLaterSubmit(data);
        try {
            await this.submitOpenTimeTrackingsFromStorage();
        } catch {
            // The tracking is safely queued and will be retried later.
        }
    }

    private getAll(): QueuedTimeExpenditureData[] {
        const storedData: Array<
            TimeExpenditureData & { trackingEventId?: string }
        > = JSON.parse(localStorage.getItem(this.STORAGE_KEY)) ?? [];
        let wasMigrated = false;
        const normalizedData = storedData.map(data => {
            if (data.trackingEventId) {
                return data as QueuedTimeExpenditureData;
            }
            wasMigrated = true;
            return { ...data, trackingEventId: uuidv4() };
        });
        if (wasMigrated) {
            this.replaceAll(normalizedData);
        }
        return normalizedData;
    }

    private replaceAll(data: QueuedTimeExpenditureData[] = []) {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    }

    private hasOpenTimeTrackings(): boolean {
        return this.getAll().length > 0;
    }

    private persistForLaterSubmit(data: TimeExpenditureData): void {
        this.replaceAll([
            ...this.getAll(),
            { ...data, trackingEventId: uuidv4() },
        ]);
    }

    private submitOpenTimeTrackingsFromStorage(): Promise<void> {
        const submission = this.submissionQueue.then(() =>
            this.drainOpenTimeTrackingsFromStorage()
        );
        this.submissionQueue = submission.catch(() => undefined);
        return submission;
    }

    private async drainOpenTimeTrackingsFromStorage(): Promise<void> {
        if (
            this.networkStatusService.isOffline ||
            !this.hasOpenTimeTrackings()
        ) {
            return;
        }
        const trackingEventIds = this.getAll().map(
            timeTracking => timeTracking.trackingEventId
        );
        let submissionError: unknown;
        for (const trackingEventId of trackingEventIds) {
            if (this.networkStatusService.isOffline) {
                break;
            }
            const timeTracking = this.getAll().find(
                data => data.trackingEventId === trackingEventId
            );
            if (!timeTracking) {
                continue;
            }
            try {
                await this.sendTimeTracking(timeTracking);
            } catch (error) {
                switch (error.status) {
                    // catch error, session data is missing information and can't get submitted
                    case HttpStatus.BAD_REQUEST:
                        break;
                    default:
                        submissionError = submissionError ?? error;
                        continue;
                }
            }
            this.remove(timeTracking.trackingEventId);
        }
        if (submissionError) {
            throw submissionError;
        }
    }

    private remove(trackingEventId: string): void {
        this.replaceAll(
            this.getAll().filter(
                data => data.trackingEventId !== trackingEventId
            )
        );
    }

    private sendTimeTracking(data: QueuedTimeExpenditureData): Promise<void> {
        const url = `${this.timeTrackingConfig.apiHost}/time-expenditure`;
        return this.http.post<void>(url, data).toPromise();
    }
}
